/* Couple favourites — server-side, identified by email entered once.
 *
 * Caches the email through `safeStorage` so the user doesn't have to
 * re-enter it on every visit. The email itself is non-sensitive — auth
 * lives in an HttpOnly cookie, never in client storage. See
 * `lib/safeStorage.js` for the security rationale.
 *
 * Provides:
 *   - useFavorites()  → { slugs, email, setEmail, toggle, isFavorited, ready }
 *   - <FavoriteHeart vendor_slug=... className=... data-testid=... />
 *
 * Public via `import { useFavorites, FavoriteHeart } from "@/lib/favorites"`.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Heart, X } from "lucide-react";
import { toast } from "sonner";
import { safeStorage } from "@/lib/safeStorage";
import { devLog } from "./log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_KEY = "ld_couple_email";
const ROLE_KEY = "ld_viewer_role";

/* Hearts are a couple-facing feature only. Vendors shouldn't see/use them
 * when browsing other vendors' work. Reads the cached role written by
 * fetchMe() in lib/auth.js. Anonymous viewers (no cached role) continue
 * to see hearts so they can save items pre-signup. */
const viewerIsVendor = () => safeStorage.get(ROLE_KEY) === "vendor";

/* ---------------------------- Hook --------------------------------- */
export const useFavorites = () => {
  const [email, setEmailState] = useState(() => safeStorage.get(EMAIL_KEY) || "");
  const [slugs, setSlugs] = useState(new Set());
  const [ready, setReady] = useState(false);

  // Load favourites whenever the email changes.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!email) {
        setSlugs(new Set());
        setReady(true);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/favorites`, {
          params: { email },
        });
        if (!cancelled) setSlugs(new Set(data.slugs || []));
      } catch (e) {
        if (!cancelled) setSlugs(new Set());
      } finally {
        if (!cancelled) setReady(true);
      }
    };
    setReady(false);
    load();
    return () => {
      cancelled = true;
    };
  }, [email]);

  const setEmail = useCallback((value) => {
    const v = (value || "").trim().toLowerCase();
    if (v) safeStorage.set(EMAIL_KEY, v);
    else safeStorage.remove(EMAIL_KEY);
    setEmailState(v);
  }, []);

  const toggle = useCallback(
    async (slug) => {
      if (!email) return { ok: false, needsEmail: true };
      // Optimistic update
      const had = slugs.has(slug);
      const next = new Set(slugs);
      if (had) next.delete(slug);
      else next.add(slug);
      setSlugs(next);
      try {
        const { data } = await axios.post(`${API}/favorites/toggle`, {
          email,
          vendor_slug: slug,
        });
        // Reconcile with server result
        const after = new Set(slugs);
        if (data.favorited) after.add(slug);
        else after.delete(slug);
        setSlugs(after);
        return { ok: true, favorited: data.favorited };
      } catch (e) {
        // Roll back
        setSlugs(slugs);
        toast("Couldn't update favourite", { duration: 2200 });
        return { ok: false };
      }
    },
    [email, slugs]
  );

  const isFavorited = useCallback((slug) => slugs.has(slug), [slugs]);

  return { email, setEmail, slugs, toggle, isFavorited, ready };
};

/* ------------------------ Media favourites ------------------------ *
 * Same shape as useFavorites but identifies individual media items
 * (image/video URLs scoped to a vendor_slug) instead of whole vendors.
 * Used by MediaFavoriteHeart on vendor galleries and the "Saved Media"
 * section of /my-home.
 */
export const useMediaFavorites = () => {
  const [email, setEmailState] = useState(() => safeStorage.get(EMAIL_KEY) || "");
  const [items, setItems] = useState([]); // [{vendor_slug, media_url, media_type, created_at}]
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!email) {
        setItems([]);
        setReady(true);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/favorites/media`, {
          params: { email },
        });
        if (!cancelled) setItems(data.items || []);
      } catch (e) {
        devLog("ld_mediaFavs load failed:", e?.message);
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setReady(true);
      }
    };
    setReady(false);
    load();
    return () => {
      cancelled = true;
    };
  }, [email]);

  const setEmail = useCallback((value) => {
    const v = (value || "").trim().toLowerCase();
    if (v) safeStorage.set(EMAIL_KEY, v);
    setEmailState(v);
  }, []);

  const isFavorited = useCallback(
    (slug, url) => items.some((i) => i.vendor_slug === slug && i.media_url === url),
    [items]
  );

  const toggle = useCallback(
    async (slug, url, type = "image") => {
      if (!email) return { ok: false, needsEmail: true };
      const had = isFavorited(slug, url);
      // Optimistic update
      const next = had
        ? items.filter((i) => !(i.vendor_slug === slug && i.media_url === url))
        : [{ vendor_slug: slug, media_url: url, media_type: type, created_at: new Date().toISOString() }, ...items];
      setItems(next);
      try {
        const { data } = await axios.post(`${API}/favorites/media/toggle`, {
          email,
          vendor_slug: slug,
          media_url: url,
          media_type: type,
        });
        return { ok: true, favorited: data.favorited };
      } catch {
        setItems(items); // roll back
        toast("Couldn't update favourite", { duration: 2200 });
        return { ok: false };
      }
    },
    [email, items, isFavorited]
  );

  return { email, setEmail, items, toggle, isFavorited, ready };
};

/* ---------------------------- Email modal -------------------------- */
const EmailPromptModal = ({ open, onClose, onSubmit }) => {
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) setValue("");
  }, [open]);

  if (!open) return null;

  const handle = async (e) => {
    e.preventDefault();
    const v = value.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      toast("Please enter a valid email", { duration: 2000 });
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(v);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      data-testid="favorite-email-modal"
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/55 backdrop-blur-sm px-5"
      onClick={onClose}
    >
      <form
        onSubmit={handle}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md bg-[var(--ld-ivory)] border border-[var(--ld-border)] p-7 sm:p-8"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 p-1 text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)]"
        >
          <X size={16} strokeWidth={1.6} />
        </button>
        <span className="font-accent text-[10px] tracking-[0.3em] uppercase text-[var(--ld-gold-deep)]">
          Save your favourites
        </span>
        <h3 className="font-serif-display text-2xl text-[var(--ld-ink)] mt-2 leading-tight">
          One quick step.
        </h3>
        <p className="mt-3 text-[13px] leading-[1.7] text-[var(--ld-ink-muted)]">
          Enter your email so we can save your favourite vendors. Next time
          you&apos;ll see them all in one place.
        </p>
        <input
          type="email"
          required
          autoFocus
          value={value}
          data-testid="favorite-email-input"
          onChange={(e) => setValue(e.target.value)}
          placeholder="you@example.com"
          className="mt-5 w-full h-11 bg-transparent border-b border-[var(--ld-ink)]/40 px-1 text-[15px] text-[var(--ld-ink)] focus:outline-none focus:border-[var(--ld-gold-deep)]"
        />
        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-[11px] tracking-[0.2em] uppercase text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)] px-3 py-2"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            data-testid="favorite-email-submit"
            className="text-[11px] tracking-[0.2em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] px-4 py-2 hover:opacity-90 disabled:opacity-60"
          >
            {submitting ? "Saving…" : "Save favourite"}
          </button>
        </div>
      </form>
    </div>
  );
};

/* ---------------------------- Heart button ------------------------- */
export const FavoriteHeart = ({
  vendor_slug,
  className = "",
  size = 16,
  testid,
}) => {
  const { email, setEmail, toggle, isFavorited } = useFavorites();
  const [promptOpen, setPromptOpen] = useState(false);
  const active = isFavorited(vendor_slug);

  // Vendors shouldn't see hearts when browsing the directory.
  if (viewerIsVendor()) return null;

  const handleClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!email) {
      setPromptOpen(true);
      return;
    }
    const res = await toggle(vendor_slug);
    if (res.ok) {
      toast(res.favorited ? "Saved to favourites" : "Removed from favourites", {
        duration: 1800,
      });
    }
  };

  const handleSubmitEmail = async (v) => {
    setEmail(v);
    setPromptOpen(false);
    // After the email is set, toggle the favourite on first visit.
    try {
      const { data } = await axios.post(`${API}/favorites/toggle`, {
        email: v,
        vendor_slug,
      });
      toast(data.favorited ? "Saved to favourites" : "Removed", {
        duration: 1800,
      });
    } catch {
      toast("Couldn't save favourite", { duration: 2000 });
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        aria-label={active ? "Remove from favourites" : "Save to favourites"}
        aria-pressed={active}
        data-testid={testid || `favorite-heart-${vendor_slug}`}
        className={
          "inline-flex items-center justify-center bg-black/55 text-white/90 backdrop-blur-sm hover:bg-black/80 transition-colors " +
          className
        }
      >
        <Heart
          size={size}
          strokeWidth={1.8}
          className={
            "transition-transform duration-300 " +
            (active ? "scale-110 text-rose-400 fill-rose-400" : "")
          }
          fill={active ? "currentColor" : "none"}
        />
      </button>
      <EmailPromptModal
        open={promptOpen}
        onClose={() => setPromptOpen(false)}
        onSubmit={handleSubmitEmail}
      />
    </>
  );
};

/* ---------------------- Media heart button ------------------------- *
 * Same UX as FavoriteHeart, but identifies a single media item (URL)
 * scoped to a vendor. Drop one of these on top of every image/video
 * tile inside a vendor profile.
 */
export const MediaFavoriteHeart = ({
  vendor_slug,
  media_url,
  media_type = "image",
  className = "",
  size = 14,
  testid,
}) => {
  const { email, setEmail, toggle, isFavorited } = useMediaFavorites();
  const [promptOpen, setPromptOpen] = useState(false);
  const active = isFavorited(vendor_slug, media_url);

  // Vendors shouldn't see hearts when browsing the directory.
  if (viewerIsVendor()) return null;

  const handleClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!email) {
      setPromptOpen(true);
      return;
    }
    const res = await toggle(vendor_slug, media_url, media_type);
    if (res.ok) {
      toast(res.favorited ? "Saved to My Home" : "Removed from My Home", {
        duration: 1800,
      });
    }
  };

  const handleSubmitEmail = async (v) => {
    setEmail(v);
    setPromptOpen(false);
    try {
      const { data } = await axios.post(`${API}/favorites/media/toggle`, {
        email: v,
        vendor_slug,
        media_url,
        media_type,
      });
      toast(data.favorited ? "Saved to My Home" : "Removed", { duration: 1800 });
    } catch {
      toast("Couldn't save favourite", { duration: 2000 });
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        aria-label={active ? "Remove from My Home" : "Save to My Home"}
        aria-pressed={active}
        data-testid={
          testid ||
          `media-favorite-${vendor_slug}-${encodeURIComponent(media_url).slice(-24)}`
        }
        className={
          "inline-flex items-center justify-center bg-black/55 text-white/90 backdrop-blur-sm hover:bg-black/80 transition-colors " +
          className
        }
      >
        <Heart
          size={size}
          strokeWidth={1.8}
          className={
            "transition-transform duration-300 " +
            (active ? "scale-110 text-rose-400 fill-rose-400" : "")
          }
          fill={active ? "currentColor" : "none"}
        />
      </button>
      <EmailPromptModal
        open={promptOpen}
        onClose={() => setPromptOpen(false)}
        onSubmit={handleSubmitEmail}
      />
    </>
  );
};
